import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { Storage } from 'react-jhipster';
import { BackgroundExportManager, isMobileDevice } from 'app/modules/reports/background-export-manager';
import logoImage from '../../../content/images/img.png';

export interface TableCheckboxStates {
  poChecked: boolean;
  customerChecked: boolean;
  commodityChecked: boolean;
  currencyChecked: boolean;
  quantityChecked: boolean;
  amountChecked: boolean;
  feeChecked: boolean;
  feeCurrencyChecked: boolean;
  netAmountChecked: boolean;
  ibanNumberChecked: boolean;
  soldByChecked: boolean;
  statusChecked: boolean;
  amountTransferredChecked: boolean;
}

export interface ReportHelperFunctions {
  translateStatus: (q: any) => string;
  cancelledTransaction: (q: any) => string;
  getStaffFullName: (createdUser: any) => string;
}

/**
 * Add English headers with logo
 */
export function addEnglishHeaders(pdf: jsPDF, pageWidth: number, marginOuter: number, currentY: number): void {
  pdf.setFontSize(25);
  pdf.text('Sales Activity Report', marginOuter, currentY + 8);

  const imgWidth = 75;
  const imgHeight = 10;

  try {
    pdf.addImage(logoImage, 'PNG', pageWidth - marginOuter - imgWidth, currentY, imgWidth, imgHeight, undefined, 'FAST');
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error);
  }
}

export function addArabicHeaders(pdf: jsPDF, pageWidth: number, marginOuter: number, currentY: number, fontName = 'Amiri-Regular'): void {
  const imgWidth = 75;
  const imgHeight = 10;

  try {
    pdf.addImage(logoImage, 'PNG', marginOuter, currentY, imgWidth, imgHeight, undefined, 'FAST');
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error);
  }

  pdf.setFont(fontName, 'normal');
  pdf.setFontSize(25);

  const textWidth = pdf.getTextWidth('تقرير نشاط المبيعات');
  pdf.text('تقرير نشاط المبيعات', pageWidth - marginOuter - textWidth, currentY + 8);
}

/**
 * Add info sections (Order and Company/Bank info)
 */
export function addInfoSections(
  pdf: jsPDF,
  isArabic: boolean,
  pageWidth: number,
  marginOuter: number,
  currentY: number,
  fontName?: string
): number {
  pdf.setFontSize(14);

  if (isArabic) {
    // Arabic info sections (RTL)
    if (fontName) pdf.setFont(fontName, 'normal');

    const leftColumnX = marginOuter + 75;
    const lineSpacing = 8;
    let localY = currentY;

    // Company info (left side for Arabic)
    let text = 'معلومات الشركة/البنك';
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${Storage.local.get('bank')} : الجهة`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${localStorage.getItem('branch')} : الفرع`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);
    localY += lineSpacing;

    text = `${localStorage.getItem('userName')} : المستخدم`;
    pdf.text(text, leftColumnX - pdf.getTextWidth(text), localY);

    // Order info (right side for Arabic)
    const rightColumnX = pageWidth - marginOuter;
    let orderInfoY = currentY;

    if (fontName) pdf.setFont(fontName, 'normal');
    text = 'طلب';
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);
    orderInfoY += lineSpacing;

    text = `${localStorage.getItem('status')} : نوع الحالة`;
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);
    orderInfoY += lineSpacing;

    text = `${localStorage.getItem('commodity')} : السلعة`;
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);
    orderInfoY += lineSpacing;

    text = `${localStorage.getItem('period')} : فترة`;
    pdf.text(text, rightColumnX - pdf.getTextWidth(text), orderInfoY);

    return localY;
  } else {
    // English info sections (LTR)
    let localY = currentY;

    pdf.setFont(undefined, 'bold');
    pdf.text('Order', marginOuter, localY);
    pdf.setFont(undefined, 'normal');
    localY += 6;
    pdf.text(`Status Type : ${localStorage.getItem('status')}`, marginOuter, localY);
    localY += 6;
    pdf.text(`Commodity : ${localStorage.getItem('commodity')}`, marginOuter, localY);
    localY += 6;
    pdf.text(`Period : ${localStorage.getItem('period')}`, marginOuter, localY);

    const bankInfoX = pageWidth - marginOuter - 75;
    let bankInfoY = currentY;
    pdf.setFont(undefined, 'bold');
    pdf.text('Company/ Bank', bankInfoX, bankInfoY);
    pdf.setFont(undefined, 'normal');
    bankInfoY += 6;
    pdf.text(`Name : ${Storage.local.get('bank')}`, bankInfoX, bankInfoY);
    bankInfoY += 6;
    pdf.text(`Branch : ${localStorage.getItem('branch')}`, bankInfoX, bankInfoY);
    bankInfoY += 6;
    pdf.text(`User : ${localStorage.getItem('userName')}`, bankInfoX, bankInfoY);

    return localY;
  }
}

/**
 * Get table headers based on language and checkbox states
 */
export function getTableHeaders(isArabic: boolean, checkboxStates: TableCheckboxStates): string[][] {
  const {
    poChecked,
    customerChecked,
    commodityChecked,
    currencyChecked,
    quantityChecked,
    amountChecked,
    feeChecked,
    feeCurrencyChecked,
    netAmountChecked,
    ibanNumberChecked,
    soldByChecked,
    statusChecked,
    amountTransferredChecked,
  } = checkboxStates;

  if (isArabic) {
    return [
      [
        amountTransferredChecked ? 'المبلغ المحول' : '',
        statusChecked ? 'الحالة' : '',
        soldByChecked ? 'البائع' : '',
        ibanNumberChecked ? 'رقم الآيبان' : '',
        netAmountChecked ? 'المبلغ الصافي' : '',
        feeCurrencyChecked ? 'عملة الرسوم' : '',
        feeChecked ? 'مصاريف' : '',
        amountChecked ? 'كمية' : '',
        quantityChecked ? 'الكمية (طن)' : '',
        currencyChecked ? 'عملة' : '',
        commodityChecked ? 'السلعة' : '',
        customerChecked ? 'اسم المستخدم' : '',
        customerChecked ? 'اسم الفرع' : '',
        customerChecked ? 'البنك/الشركة' : '',
        'بيع',
        'تحويل',
        'شراء',
        customerChecked ? 'رقم المدني بالعميل' : '',
        customerChecked ? 'العميل' : '',
        poChecked ? 'رقم المرجع' : '',
      ],
    ];
  } else {
    return [
      [
        poChecked ? 'Reference Number' : '',
        customerChecked ? 'Customer Name' : '',
        customerChecked ? 'Customer ID' : '',
        'Buy',
        'Transfer',
        'Sell',
        customerChecked ? 'Bank Name' : '',
        customerChecked ? 'Branch Name' : '',
        customerChecked ? 'Bank Staff Name' : '',
        commodityChecked ? 'Commodity' : '',
        currencyChecked ? 'Currency' : '',
        quantityChecked ? 'Quantity (T)' : '',
        amountChecked ? 'Amount' : '',
        feeChecked ? 'Fee' : '',
        feeCurrencyChecked ? 'Fee Currency' : '',
        netAmountChecked ? 'Net Amount' : '',
        ibanNumberChecked ? 'IBAN Number' : '',
        soldByChecked ? 'Sold By' : '',
        statusChecked ? 'Status' : '',
        amountTransferredChecked ? 'Amount Transferred' : '',
      ],
    ];
  }
}

/**
 * Process table data for PDF
 */
export async function processTableData(
  allData: any[],
  isArabic: boolean,
  checkboxStates: TableCheckboxStates,
  helpers: ReportHelperFunctions
): Promise<any[]> {
  const exportManager = BackgroundExportManager.getInstance();
  const processedData: any[] = [];
  const PROCESS_BATCH_SIZE = isMobileDevice() ? 50 : 200;

  const {
    poChecked,
    customerChecked,
    commodityChecked,
    currencyChecked,
    quantityChecked,
    amountChecked,
    feeChecked,
    feeCurrencyChecked,
    netAmountChecked,
    ibanNumberChecked,
    soldByChecked,
    statusChecked,
    amountTransferredChecked,
  } = checkboxStates;

  const { translateStatus, cancelledTransaction, getStaffFullName } = helpers;

  await exportManager.processInBatches(allData, PROCESS_BATCH_SIZE, batch => {
    batch.forEach(q => {
      const rowData = isArabic
        ? [
            amountTransferredChecked ? (q?.amountTransferred ? 'نعم' : '') : '',
            statusChecked ? translateStatus(q) : '',
            soldByChecked ? (q?.soldBy || '-') : '',
            ibanNumberChecked ? (q?.iban?.ibanNumber !== null ? q?.iban?.ibanNumber : '-') : '',
            netAmountChecked ? (q?.amount != null ? (q?.amount - ((q?.fee ?? 0) + (q?.commission ?? 0))).toLocaleString() : '-') : '',
            feeCurrencyChecked ? q?.feeCurrencyIndex || '' : '',
            feeChecked ? cancelledTransaction(q) : '',
            amountChecked ? q?.amount?.toLocaleString() || '' : '',
            quantityChecked ? q?.commodityQuantity?.toLocaleString() || '' : '',
            currencyChecked ? q?.buyCurrency || '' : '',
            commodityChecked ? q?.commodity?.name || '' : '',
            customerChecked ? getStaffFullName(q?.createdUser) || '' : '',
            customerChecked
              ? `${q?.branch?.name || ''} ${q?.branch?.createdDate ? moment(q?.branch?.createdDate).format('yyyy/MM/DD') : ''}`
              : '',
            customerChecked ? q?.bank?.name || '' : '',
            q?.bursaAPIStbCertId !== null
              ? moment(q?.bursaAPIStbCert?.sellingTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityStbCertId !== null
              ? moment.utc(q?.localCommodityStbCert?.sellingTimeDate, 'HH:mm:ss.SSS DD MMM YYYY').local().format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            q?.bursaAPIOtcCertId !== null
              ? moment(q?.bursaAPIOtcCert?.reportingTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityOtcCertId !== null
              ? moment
                  .utc(q?.localCommodityOtcCert?.reportingTimeDate, 'HH:mm:ss.SSS DD MMM YYYY')
                  .local()
                  .format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            q?.bursaAPIBidCertId !== null
              ? moment(q?.bursaAPIBidCert?.purchaseTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityBidCertId !== null
              ? moment.utc(q?.localCommodityBidCert?.purchaseTimeDate, 'HH:mm:ss.SSS DD MMM YYYY').local().format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            customerChecked ? q?.customer?.ic || '' : '',
            customerChecked
              ? q?.commodity?.ownership === 'Local' && q?.customer?.nameArabic
                ? q?.customer?.nameArabic
                : q?.customer?.name
              : '',
            poChecked
              ? q?.bursaAPIBidCertId !== null
                ? q?.bursaAPIBidCert?.ecertNo
                : q?.commodity?.ownership === 'Local'
                ? q?.transactionRefNo || '-'
                : '-'
              : '',
          ]
        : [
            poChecked
              ? q?.bursaAPIBidCertId !== null
                ? q?.bursaAPIBidCert?.ecertNo
                : q?.commodity?.ownership === 'Local'
                ? q?.transactionRefNo || '-'
                : '-'
              : '',
            customerChecked
              ? q?.commodity?.ownership === 'Local' && q?.customer?.nameArabic
                ? q?.customer?.nameArabic
                : q?.customer?.name
              : '',
            customerChecked ? q?.customer?.ic || '' : '',
            q?.bursaAPIBidCertId !== null
              ? moment(q?.bursaAPIBidCert?.purchaseTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityBidCertId !== null
              ? moment.utc(q?.localCommodityBidCert?.purchaseTimeDate, 'HH:mm:ss.SSS DD MMM YYYY').local().format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            q?.bursaAPIOtcCertId !== null
              ? moment(q?.bursaAPIOtcCert?.reportingTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityOtcCertId !== null
              ? moment
                  .utc(q?.localCommodityOtcCert?.reportingTimeDate, 'HH:mm:ss.SSS DD MMM YYYY')
                  .local()
                  .format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            q?.bursaAPIStbCertId !== null
              ? moment(q?.bursaAPIStbCert?.sellingTimeDate).format('yyyy/MM/DD HH:mm:ss.SSS')
              : q?.localCommodityStbCertId !== null
              ? moment.utc(q?.localCommodityStbCert?.sellingTimeDate, 'HH:mm:ss.SSS DD MMM YYYY').local().format('yyyy/MM/DD HH:mm:ss.SSS')
              : '-',
            customerChecked ? q?.bank?.name || '' : '',
            customerChecked
              ? `${q?.branch?.name || ''} ${q?.branch?.createdDate ? moment(q?.branch?.createdDate).format('yyyy/MM/DD') : ''}`
              : '',
            customerChecked ? getStaffFullName(q?.createdUser) || '' : '',
            commodityChecked ? q?.commodity?.name || '' : '',
            currencyChecked ? q?.buyCurrency || '' : '',
            quantityChecked ? q?.commodityQuantity?.toLocaleString() || '' : '',
            amountChecked ? q?.amount?.toLocaleString() || '' : '',
            feeChecked ? cancelledTransaction(q) : '',
            feeCurrencyChecked ? q?.feeCurrencyIndex || '' : '',
            netAmountChecked ? (q?.amount != null ? (q?.amount - ((q?.fee ?? 0) + (q?.commission ?? 0))).toLocaleString() : '-') : '',
            ibanNumberChecked ? (q?.iban?.ibanNumber !== null ? q?.iban?.ibanNumber : '-') : '',
            soldByChecked ? (q?.soldBy || '-') : '',
            statusChecked ? translateStatus(q) : '',
            amountTransferredChecked ? (q?.amountTransferred ? 'Yes' : '') : '',
          ];

      processedData.push(rowData);
    });
  });

  return processedData;
}

/**
 * Get column styles for table
 */
export function getColumnStyles(isArabic: boolean): any {
  const baseStyles: any = {
    0: { cellWidth: 20 },
    1: { cellWidth: 18 },
    2: { cellWidth: 18 },
    3: { cellWidth: 15 },
    4: { cellWidth: 15 },
    5: { cellWidth: 15 },
    6: { cellWidth: 18 },
    7: { cellWidth: 18 },
    8: { cellWidth: 18 },
    9: { cellWidth: 18 },
    10: { cellWidth: 18 },
    11: { cellWidth: 18 },
    12: { cellWidth: 15 },
    13: { cellWidth: 15 },
    14: { cellWidth: 20 },
    15: { cellWidth: 15 },
    16: { cellWidth: 20 },
    17: { cellWidth: 20 },
    18: { cellWidth: 20 },
    19: { cellWidth: 20 },
  };

  // Add RTL alignment for Arabic
  if (isArabic) {
    Object.keys(baseStyles).forEach(key => {
      baseStyles[key] = { ...baseStyles[key], halign: 'right' };
    });
  }

  return baseStyles;
}

/**
 * Add summary section at the end of PDF
 */
export function addSummarySection(
  pdf: jsPDF,
  isArabic: boolean,
  pageWidth: number,
  marginOuter: number,
  currentY: number,
  translate: (key: string) => string,
  fontName?: string
): number {
  pdf.setFontSize(14);

  if (isArabic) {
    if (fontName) pdf.setFont(fontName, 'normal');
    const summaryText = 'ملخص / توقعات أداء المبيعات والأنشطة';
    pdf.text(summaryText, pageWidth - marginOuter - pdf.getTextWidth(summaryText), currentY);

    currentY += 5;
    pdf.setLineWidth(0.2);
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 10;
    const commentText = 'تعليقات على أداء الموظفين والعملاء';
    pdf.text(commentText, pageWidth - marginOuter - pdf.getTextWidth(commentText), currentY);

    currentY += 5;
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 30;
    pdf.setFontSize(14);
    const sigText = '  _________________________ :التوقيع ';
    pdf.text(sigText, pageWidth - marginOuter - pdf.getTextWidth(sigText), currentY);
  } else {
    pdf.text(translate('global.menu.report-print.summary'), marginOuter, currentY);
    pdf.setFont(undefined, 'normal');

    currentY += 5;
    pdf.setLineWidth(0.2);
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 10;
    pdf.text(translate('global.menu.report-print.comment'), marginOuter, currentY);
    pdf.setFont(undefined, 'normal');

    currentY += 5;
    pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

    currentY += 30;
    pdf.setFontSize(14);
    pdf.text(`${translate('global.menu.report-print.signature')} : _________________________`, marginOuter, currentY);
  }

  // Final divider
  currentY += 15;
  pdf.setDrawColor(1, 87, 155);
  pdf.setLineWidth(1);
  pdf.line(marginOuter, currentY, pageWidth - marginOuter, currentY);

  return currentY;
}

/**
 * Add page numbers to PDF
 */
export function addPageNumbers(pdf: jsPDF, isArabic: boolean, marginOuter: number): void {
  const pageCount = pdf.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(10);
    const pageHeight = pdf.internal.pageSize.height;

    if (isArabic) {
      pdf.setFont('Amiri-Regular', 'normal');

      const pageText = `صفحة ${i} من ${pageCount}`;

      const pageWidth = pdf.internal.pageSize.width;
      pdf.text(pageText, pageWidth - marginOuter, pageHeight - 5, { align: 'right' });

      pdf.setFont('helvetica', 'normal');
    } else {
      const pageText = `Page ${i} of ${pageCount}`;
      pdf.text(pageText, marginOuter, pageHeight - 5);
    }
  }
}
